import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import CourseLanguageDialog from '../components/CourseLanguageDialog';
import CourseProgressBar from '../components/CourseProgressBar';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {
  certificateAPI,
  courseAPI,
  fetchCourseLockStatusFromList,
} from '../services/api';
import '../styles/components/_course-detail.scss';
import { getCourseAvailableLanguages, isCourseLanguageAvailable } from '../utils/courseLanguages';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import {
  BookIcon,
  BarChart2Icon,
  ChapterIcon,
  CheckCircleIcon,
  CloseIcon,
  EditIcon,
  FilmIcon,
  FlagIcon,
  InfoIcon,
  LockIcon,
  TrophyIcon,
  WarningIcon,
  WorkshopIcon
} from '../utils/icons';
import { normalizeId } from '../utils/id';
import { arePriorChaptersCompleted, isChapterFullyCompleted } from '../utils/chapterAccess';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import {
  dismissWelcomeMessage,
  hasWelcomeMessageContent,
  isWelcomeMessageDismissed,
} from '../utils/welcomeMessage';

interface CourseChapter {
  _id: string;
  title: string;
  description?: string | { [key: string]: string };
  summary?: string;
  capsules?: any[];
  workshops?: any[];
  items?: any[]; // All items (capsules, quiz, workshops)
  order: number;
  isCompleted?: boolean;
  isLocked?: boolean;
  unlockDate?: string; // ISO date string
  deadline?: string; // ISO date string
  icon?: 'flag' | 'people' | 'award' | 'book' | 'film' | 'edit';
  totalDuration?: number;
}

interface AssessmentEligibility {
  diagnosticRequired: boolean;
  diagnosticSubmitted: boolean;
  finalImpactAvailable: boolean;
  finalImpactSubmitted: boolean;
  finalImpactBlockedReason: 'workshops' | 'chapters' | 'diagnostic' | 'feature-disabled' | null;
  finalImpactExpectedAfter?: string | null;
  finalImpactUnlockRule?: 'after-last-workshop' | 'when-course-complete';
}

interface CourseAssessmentPayload {
  enabled: boolean;
  eligibility: AssessmentEligibility;
  initial: { submitted: boolean; submittedAt: string | null } | null;
  final: { submitted: boolean; submittedAt: string | null } | null;
  followups: Array<{
    id: string;
    label: any;
    description?: any;
    order: number;
    unlockDate: string | null;
    available: boolean;
    /** Date de suivi atteinte mais diagnostic initial encore à faire. */
    pendingDiagnostic?: boolean;
    submitted: boolean;
  }>;
}

interface Course {
  _id: string;
  title: string;
  description: string;
  image?: string;
  welcomeMessage?: string | { [key: string]: string };
  groupId?: string | null;
  chapters: CourseChapter[];
  progress: number;
  totalDuration: number;
  diagnosticImpactEnabled?: boolean;
  assessment?: CourseAssessmentPayload | null;
}

/** Mesures d'impact à faire : fin de formation et/ou suivis M+. */
function getPendingImpactMeasures(course: Course | null): {
  pendingFinal: boolean;
  pendingFollowups: CourseAssessmentPayload['followups'];
} | null {
  const a = course?.assessment;
  if (!a?.enabled) return null;
  const pendingFinal = Boolean(
    !a.final?.submitted && a.eligibility?.finalImpactAvailable
  );
  const pendingFollowups = (a.followups || [])
    .filter((f) => f.available && !f.submitted)
    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
  if (!pendingFinal && pendingFollowups.length === 0) return null;
  return { pendingFinal, pendingFollowups };
}

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [courseLockedUntil, setCourseLockedUntil] = useState<string | null>(null);
  const [impactDialogOpen, setImpactDialogOpen] = useState(false);
  const [certEligible, setCertEligible] = useState(false);
  const [certCourseType, setCertCourseType] = useState<string | null>(null);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  useEffect(() => {
    if (!course) return;
    const currentLang = i18n.language.split('-')[0] || 'fr';
    const courseLanguages = getCourseAvailableLanguages(course);
    setAvailableLanguages(courseLanguages);
    setLanguageDialogOpen(!isCourseLanguageAvailable(course, currentLang) && courseLanguages.length > 0);
  }, [course, i18n.language]);

  const dismissImpactPromptForSession = () => {
    if (courseId) {
      sessionStorage.setItem(`courseImpactPromptDismissed_${courseId}`, '1');
    }
    setImpactDialogOpen(false);
  };

  useEffect(() => {
    if (!courseId || !course) return;
    const visible =
      hasWelcomeMessageContent(course.welcomeMessage) &&
      !isWelcomeMessageDismissed(courseId, course.groupId, course.welcomeMessage);
    setShowWelcome(visible);
  }, [course, courseId]);

  const handleDismissWelcome = () => {
    if (!courseId || !course?.welcomeMessage) return;
    dismissWelcomeMessage(courseId, course.groupId, course.welcomeMessage);
    setShowWelcome(false);
  };

  useEffect(() => {
    if (!course || !courseId) return;
    const pending = getPendingImpactMeasures(course);
    if (!pending) {
      sessionStorage.removeItem(`courseImpactPromptDismissed_${courseId}`);
      return;
    }
    if (sessionStorage.getItem(`courseImpactPromptDismissed_${courseId}`)) return;
    setImpactDialogOpen(true);
  }, [course, courseId]);

  useEffect(() => {
    if (!courseId || !course) return;
    let active = true;
    certificateAPI
      .getEligibility(courseId)
      .then((r) => {
        if (!active) return;
        setCertEligible(!!r.eligible);
        setCertCourseType(r.courseType);
      })
      .catch(() => {
        if (active) setCertEligible(false);
      });
    return () => {
      active = false;
    };
  }, [courseId, course]);

  const handleDownloadCertificate = async () => {
    if (!courseId) return;
    try {
      setCertError(null);
      setDownloadingCert(true);
      const baseName = (getLocalizedText(course?.title) || 'formation')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
      await certificateAPI.download(courseId, `certificat-${baseName || 'formation'}.pdf`);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setCertError(t('courseDetail.certificateError'));
    } finally {
      setDownloadingCert(false);
    }
  };

  const showLockedCourseScreen = (unlockDate: string | null) => {
    setCourseLockedUntil(unlockDate);
    setCourse(null);
  };

  const tryShowLockedFromList = async (): Promise<boolean> => {
    if (!courseId) return false;
    const lock = await fetchCourseLockStatusFromList(courseId);
    if (lock.locked) {
      showLockedCourseScreen(lock.unlockDate);
      return true;
    }
    return false;
  };

  const loadCourse = async () => {
    try {
      setError(null);
      setCourseLockedUntil(null);
      const data = await courseAPI.getCourse(courseId!);
      console.log('📚 Course data loaded:', data);
      console.log('📚 Chapters with items:', data.chapters?.map((ch: any) => ({
        _id: ch._id,
        title: ch.title,
        itemsCount: ch.items?.length || 0,
        items: ch.items?.map((item: any) => ({
          type: item.itemType,
          order: item.order,
          title: item.title,
          _id: item._id
        })) || [],
        capsulesCount: ch.capsules?.length || 0,
        workshopsCount: ch.workshops?.length || 0
      })));

      // Réponse incomplète : repli liste (ex. CloudFront pas encore corrigé → HTML à la place du 403).
      if (!data || !Array.isArray(data.chapters)) {
        console.warn('⚠️ Réponse formation incomplète, repli liste formations…', {
          type: typeof data,
          keys: data && typeof data === 'object' ? Object.keys(data) : null,
        });
        if (await tryShowLockedFromList()) return;
        setCourse(null);
        setError(t('courseDetail.loadingError'));
        return;
      }

      // Blindage : items toujours normalisés en tableau pour un rendu sûr de la timeline.
      const normalizedCourse = {
        ...data,
        chapters: data.chapters.map((ch: any) => ({
          ...ch,
          items: Array.isArray(ch?.items) ? ch.items : [],
        })),
      };
      setCourse(normalizedCourse);
      const currentLang = i18n.language.split('-')[0] || 'fr';
      const courseLanguages = getCourseAvailableLanguages(data);
      setAvailableLanguages(courseLanguages);
      setLanguageDialogOpen(!isCourseLanguageAvailable(data, currentLang) && courseLanguages.length > 0);
    } catch (err: unknown) {
      console.error('Error loading course:', err);

      // Chemin nominal : le backend renvoie 403 + { code: 'COURSE_LOCKED', unlockDate }.
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 403 &&
        err.response?.data?.code === 'COURSE_LOCKED'
      ) {
        showLockedCourseScreen(err.response.data.unlockDate || null);
        return;
      }

      // Repli transitoire si CloudFront renvoie encore index.html au lieu du 403 JSON.
      const isHtmlResponse =
        (err as { code?: string })?.code === 'API_HTML_RESPONSE' ||
        (axios.isAxiosError(err) &&
          typeof err.response?.data === 'string' &&
          err.response.data.trim().toLowerCase().startsWith('<!doctype html'));

      if (isHtmlResponse && (await tryShowLockedFromList())) {
        return;
      }

      if (axios.isAxiosError(err) && err.response?.status === 401) {
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_user');
        navigate('/login');
        return;
      }

      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setError(getLearnerFacingApiError(err, t));
        return;
      }

      setError(getLearnerFacingApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCourseLanguage = async (lang: string) => {
    await i18n.changeLanguage(lang);
    setLanguageDialogOpen(false);
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatWorkshopDate = (dateString: string): string => {
    const date = new Date(dateString);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (time: string): string => {
    // time is in HH:mm format, return as-is
    return time || '';
  };

  const formatUnlockDate = (unlockDate: string): string => {
    const date = new Date(unlockDate);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatDeadline = (deadline: string): string => {
    const date = new Date(deadline);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getChapterIcon = (chapter: CourseChapter) => {
    // Use the icon from the database if set, otherwise default to ChapterIcon
    switch (chapter.icon) {
      case 'flag':
        return <FlagIcon size={36} color="var(--brand-shell-bg)" />;
      case 'people':
        return <WorkshopIcon size={36} color="var(--brand-shell-bg)" />;
      case 'award':
        return <TrophyIcon size={36} color="var(--brand-shell-bg)" />;
      case 'book':
        return <BookIcon size={36} color="var(--brand-shell-bg)" />;
      case 'film':
        return <FilmIcon size={36} color="var(--brand-shell-bg)" />;
      case 'edit':
        return <EditIcon size={36} color="var(--brand-shell-bg)" />;
      default:
        return <ChapterIcon size={36} color="var(--brand-shell-bg)" />;
    }
  };

  // Si la feature diagnostic & impact est activée et que le diagnostic n'a pas été soumis,
  // tous les chapitres restent verrouillés (le diagnostic est l'étape n°1 obligatoire).
  const isDiagnosticBlocking = (): boolean => {
    if (!course?.assessment?.enabled) return false;
    return (
      course.assessment.eligibility.diagnosticRequired &&
      !course.assessment.eligibility.diagnosticSubmitted
    );
  };

  const getChapterStatus = (chapter: CourseChapter) => {
    if (isChapterFullyCompleted(chapter)) return 'completed';

    // Conditions de verrouillage :
    // - flag isLocked calculé par le backend (date d'ouverture)
    // - unlockDate dans le futur
    if (chapter.isLocked) return 'locked';
    if (chapter.unlockDate) {
      const unlockDate = new Date(chapter.unlockDate);
      const now = new Date();
      if (unlockDate > now) {
        return 'locked';
      }
    }

    // Diagnostic initial obligatoire non soumis
    if (isDiagnosticBlocking()) return 'locked';

    // Progression séquentielle : le chapitre précédent doit être terminé
    if (course?.chapters && !arePriorChaptersCompleted(course.chapters, chapter._id)) {
      return 'locked';
    }

    return 'available';
  };

  const handleResume = () => {
    if (!course) return;

    // Si la feature est on et le diagnostic non soumis : direction le diagnostic.
    if (isDiagnosticBlocking()) {
      navigate(`/course/${courseId}/assessment/initial`);
      return;
    }

    if (!course.chapters || course.chapters.length === 0) return;

    const progress = course.progress || 0;

    // If course is completed (100%), go to first capsule
    if (progress >= 100) {
      // Find the first capsule in the first chapter
      const firstChapter = course.chapters.find((ch: CourseChapter) => ch.order === 1);
      if (firstChapter && firstChapter.items && firstChapter.items.length > 0) {
        // Find first capsule (or first item if no capsule)
        const firstItem = firstChapter.items.find((item: any) => item.itemType === 'capsule') || firstChapter.items[0];
        if (firstItem) {
          const firstItemId = normalizeId(firstItem?._id || firstItem?.id);
          const firstChapterId = normalizeId(firstChapter._id);
          if (!firstItemId || !firstChapterId) return;
          const firstItemPath = firstItem.itemType === 'quiz'
            ? `quiz/${firstItemId}`
            : firstItem.itemType === 'workshop'
              ? `workshop/${firstItemId}`
              : `capsule/${firstItemId}`;
          navigate(`/course/${courseId}/chapter/${firstChapterId}/${firstItemPath}`);
          return;
        }
      }
      return;
    }

    // If progress is 0%, go to first item of first chapter
    if (progress === 0) {
      const firstChapter = course.chapters.find((ch: CourseChapter) => ch.order === 1);
      if (firstChapter && firstChapter.items && firstChapter.items.length > 0) {
        const firstItem = firstChapter.items[0];
        const firstItemId = normalizeId(firstItem?._id || firstItem?.id);
        const firstChapterId = normalizeId(firstChapter._id);
        if (!firstItemId || !firstChapterId) return;
        const itemPath = firstItem.itemType === 'quiz'
          ? `quiz/${firstItemId}`
          : firstItem.itemType === 'workshop'
            ? `workshop/${firstItemId}`
            : `capsule/${firstItemId}`;
        navigate(`/course/${courseId}/chapter/${firstChapterId}/${itemPath}`);
        return;
      }
      return;
    }

    // Otherwise, find the first uncompleted item
    // Sort chapters by order first
    const sortedChapters = [...(course.chapters || [])].sort((a: CourseChapter, b: CourseChapter) => a.order - b.order);

    for (const chapter of sortedChapters) {
      if (!chapter.items || chapter.items.length === 0) continue;

      // Sort items by order
      const sortedItems = [...chapter.items].sort((a: any, b: any) => a.order - b.order);

      // Find first uncompleted item
      const firstUncompleted = sortedItems.find((item: any) => !item.isCompleted);
      if (firstUncompleted) {
        const firstUncompletedId = normalizeId(firstUncompleted?._id || firstUncompleted?.id);
        const chapterId = normalizeId(chapter._id);
        if (!firstUncompletedId || !chapterId) return;
        const itemPath = firstUncompleted.itemType === 'quiz'
          ? `quiz/${firstUncompletedId}`
          : firstUncompleted.itemType === 'workshop'
            ? `workshop/${firstUncompletedId}`
            : `capsule/${firstUncompletedId}`;
        navigate(`/course/${courseId}/chapter/${chapterId}/${itemPath}`);
        return;
      }
    }

    // Tous les items sont complétés. Si la mesure d'impact est dispo et non soumise,
    // c'est l'étape logique suivante.
    const a = course.assessment;
    if (a?.enabled && !a.final?.submitted && a.eligibility?.finalImpactAvailable) {
      navigate(`/course/${courseId}/assessment/final`);
      return;
    }
  };

  const handleOpenChapter = (chapterId: string) => {
    if (!course) return;

    const chapter = course.chapters?.find(
      (ch) => normalizeId(ch._id) === chapterId
    );
    if (!chapter || getChapterStatus(chapter) === 'locked') return;
    if (!chapter.items?.length) return;

    const sortedItems = [...chapter.items].sort(
      (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
    );
    const targetItem =
      sortedItems.find((item: any) => !item.isCompleted) ?? sortedItems[0];

    const itemId = normalizeId(targetItem?._id || targetItem?.id);
    if (!itemId) return;

    const itemPath =
      targetItem.itemType === 'quiz'
        ? `quiz/${itemId}`
        : targetItem.itemType === 'workshop'
          ? `workshop/${itemId}`
          : `capsule/${itemId}`;

    navigate(`/course/${courseId}/chapter/${chapterId}/${itemPath}`);
  };

  const handleStartDiagnostic = () => {
    if (!courseId) return;
    navigate(`/course/${courseId}/assessment/initial`);
  };

  const handleStartFinalImpact = () => {
    if (!courseId) return;
    navigate(`/course/${courseId}/assessment/final`);
  };

  const goToFinalImpactFromDialog = () => {
    if (!courseId) return;
    dismissImpactPromptForSession();
    navigate(`/course/${courseId}/assessment/final`);
  };

  const goToFollowupImpactFromDialog = (followupId: string) => {
    if (!courseId) return;
    dismissImpactPromptForSession();
    navigate(`/course/${courseId}/assessment/followup/${followupId}`);
  };

  /**
   * Rend une « carte assessment » (diagnostic, mesure d'impact ou suivi M+) avec le même look
   * que les chapitres dans la timeline. Statuts : completed / available / locked.
   */
  const renderAssessmentStep = (
    kind: 'diagnostic' | 'final' | 'followup',
    options: {
      isFirst?: boolean;
      isLast?: boolean;
      followup?: CourseAssessmentPayload['followups'][number];
    } = {}
  ) => {
    if (!course?.assessment?.enabled) return null;
    if (kind === 'followup' && !options.followup) return null;

    const a = course.assessment;
    const isDiagnostic = kind === 'diagnostic';
    const isFollowup = kind === 'followup';
    const fu = options.followup;

    let status: 'completed' | 'available' | 'locked' = 'available';
    let title = '';
    const icon = <BarChart2Icon size={36} color="var(--brand-shell-bg)" />;
    let onClick: (() => void) | null = null;
    let lockedMessage: string | null = null;

    if (isDiagnostic) {
      title = t('assessment.diagnostic.title');
      if (a.initial?.submitted) {
        status = 'completed';
      } else {
        status = 'available';
        onClick = handleStartDiagnostic;
      }
    } else if (isFollowup && fu) {
      title = getLocalizedText(fu.label, t('assessment.followup.label'));
      if (fu.submitted) {
        status = 'completed';
      } else if (fu.pendingDiagnostic) {
        status = 'locked';
        lockedMessage = t('assessment.final.lockedDiagnostic');
      } else if (fu.available) {
        status = 'available';
        onClick = () => courseId && navigate(`/course/${courseId}/assessment/followup/${fu.id}`);
      } else {
        status = 'locked';
        if (fu.unlockDate) {
          lockedMessage = t('assessment.followup.scheduledFor', {
            date: formatUnlockDate(fu.unlockDate),
          });
        } else {
          lockedMessage = t('assessment.followup.notScheduled');
        }
      }
    } else {
      title = t('assessment.final.title');
      if (a.final?.submitted) {
        status = 'completed';
      } else if (a.eligibility.finalImpactAvailable) {
        status = 'available';
        onClick = handleStartFinalImpact;
      } else {
        status = 'locked';
        if (a.eligibility.finalImpactBlockedReason === 'workshops' && a.eligibility.finalImpactExpectedAfter) {
          lockedMessage = t('assessment.final.lockedWorkshops', {
            date: formatUnlockDate(a.eligibility.finalImpactExpectedAfter),
          });
        } else if (a.eligibility.finalImpactBlockedReason === 'chapters') {
          lockedMessage = t('assessment.final.lockedChapters');
        } else if (a.eligibility.finalImpactBlockedReason === 'diagnostic') {
          lockedMessage = t('assessment.final.lockedDiagnostic');
        } else {
          lockedMessage = t('assessment.final.lockedChapters');
        }
      }
    }

    const isCompleted = status === 'completed';
    const isLocked = status === 'locked';
    const clickable = !!onClick && !isLocked;

    return (
      <div>
        <div
          className={`course-detail__chapter course-detail__chapter--${status} course-detail__assessment-step`}
          onClick={clickable ? onClick! : undefined}
          role={clickable ? 'button' : undefined}
          style={{ cursor: clickable ? 'pointer' : 'default' }}
        >
          <div className="course-detail__chapter-icon-container">
            {options.isFirst ? (
              <div style={{ height: '10px' }} />
            ) : (
              <div className="course-detail__chapter-icon-traittop"></div>
            )}
            <div className="course-detail__chapter-icon">{icon}</div>
            {options.isLast ? (
              <div style={{ flex: 1 }} />
            ) : (
              <div className="course-detail__chapter-icon-trait"></div>
            )}
          </div>

          <div className={`course-detail__chapter-card course-detail__chapter-card--${status}`}>
            <div className="course-detail__chapter-content">
              <Box className="course-detail__chapter-header">
                <p className={`course-detail__chapter-title course-detail__chapter-title--${status}`}>
                  {title}
                </p>

                {isLocked && lockedMessage && (
                  <Box className="course-detail__chapter-unlock-message">
                    <InfoIcon size={16} color="#A0A0A0" />
                    <Typography variant="body2" className="course-detail__chapter-unlock-text">
                      {lockedMessage}
                    </Typography>
                  </Box>
                )}
                {isCompleted && isDiagnostic && a && (
                  <Box className="course-detail__chapter-unlock-message">
                    <CheckCircleIcon size={16} color="#00d377" />
                    <Typography variant="body2" className="course-detail__chapter-unlock-text">
                      {t('assessment.completedOn', {
                        date: formatDate(a.initial?.submittedAt || ''),
                      })}
                    </Typography>
                  </Box>
                )}
                {isCompleted && kind === 'final' && a && (
                  <Box className="course-detail__chapter-unlock-message">
                    <CheckCircleIcon size={16} color="#00d377" />
                    <Typography variant="body2" className="course-detail__chapter-unlock-text">
                      {t('assessment.completedOn', {
                        date: formatDate(a.final?.submittedAt || ''),
                      })}
                    </Typography>
                  </Box>
                )}
                {isCompleted && isFollowup && (
                  <Box className="course-detail__chapter-unlock-message">
                    <CheckCircleIcon size={16} color="#00d377" />
                    <Typography variant="body2" className="course-detail__chapter-unlock-text">
                      {t('assessment.followup.completedLabel')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </div>
          </div>
        </div>
        {!options.isLast && (
          <div className="course-detail__chapter-undertrait">
            <div className="course-detail__chapter-undertrait-line"></div>
          </div>
        )}
      </div>
    );
  };

  const renderCertificateStep = (
    options: { isFirst?: boolean; isLast?: boolean } = {}
  ) => {
    const status: 'available' | 'locked' = certEligible ? 'available' : 'locked';
    const isLocked = status === 'locked';

    return (
      <div>
        <div
          className={`course-detail__chapter course-detail__chapter--${status} course-detail__assessment-step`}
        >
          <div className="course-detail__chapter-icon-container">
            {options.isFirst ? (
              <div style={{ height: '10px' }} />
            ) : (
              <div className="course-detail__chapter-icon-traittop"></div>
            )}
            <div className="course-detail__chapter-icon">
              <TrophyIcon size={36} color="var(--brand-shell-bg)" />
            </div>
            {options.isLast ? (
              <div style={{ flex: 1 }} />
            ) : (
              <div className="course-detail__chapter-icon-trait"></div>
            )}
          </div>

          <div className={`course-detail__chapter-card course-detail__chapter-card--${status}`}>
            <div className="course-detail__chapter-content">
              <Box className="course-detail__chapter-header">
                <p className={`course-detail__chapter-title course-detail__chapter-title--${status}`}>
                  {t('courseDetail.certificate.title')}
                </p>

                {isLocked ? (
                  <Box className="course-detail__chapter-unlock-message">
                    <InfoIcon size={16} color="#A0A0A0" />
                    <Typography variant="body2" className="course-detail__chapter-unlock-text">
                      {t('courseDetail.certificate.locked')}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      className="button-primary course-detail__certificate-button"
                      onClick={handleDownloadCertificate}
                      disabled={downloadingCert}
                      sx={{ mt: 1.5 }}
                    >
                      {downloadingCert
                        ? t('courseDetail.certificate.downloading')
                        : t('courseDetail.certificate.download')}
                    </Button>
                    {certError && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {certError}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </div>
          </div>
        </div>
        {!options.isLast && (
          <div className="course-detail__chapter-undertrait">
            <div className="course-detail__chapter-undertrait-line"></div>
          </div>
        )}
      </div>
    );
  };

  const handleItemClick = (chapterId: string, item: any) => {
    const itemId = normalizeId(item?._id || item?.id);
    const safeChapterId = normalizeId(chapterId);
    if (!itemId || !safeChapterId) return;
    if (item.itemType === 'quiz') {
      navigate(`/course/${courseId}/chapter/${safeChapterId}/quiz/${itemId}`);
    } else if (item.itemType === 'capsule') {
      navigate(`/course/${courseId}/chapter/${safeChapterId}/capsule/${itemId}`);
    } else if (item.itemType === 'workshop') {
      navigate(`/course/${courseId}/chapter/${safeChapterId}/workshop/${itemId}`);
    }
  };

  const getItemTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      capsule: t('courseDetail.itemTypes.capsule'),
      quiz: t('courseDetail.itemTypes.quiz'),
      workshop: t('courseDetail.itemTypes.workshop')
    };
    return labels[type] || '';
  };

  if (loading) {
    return (
      <Box className="course-detail__loading">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (courseLockedUntil) {
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    const formatted = new Date(courseLockedUntil).toLocaleString(locale, {
      dateStyle: 'long',
      timeStyle: 'short'
    });
    return (
      <AppLayout>
        <Box className="course-detail__error" sx={{ p: 3, textAlign: 'center' }}>
          <LockIcon size={48} color="var(--brand-shell-bg)" />
          <Typography variant="h5" className="course-detail__error-title" sx={{ mt: 2 }}>
            {t('courseDetail.courseLockedTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('courseDetail.courseLockedMessage', { date: formatted })}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            className="course-detail__error-button"
          >
            {t('courseDetail.backToDashboard')}
          </Button>
        </Box>
      </AppLayout>
    );
  }

  if (!course && !loading) {
    return (
      <Box className="course-detail__error">
        <Typography variant="h4" className="course-detail__error-title">
          {error || t('courseDetail.notFound')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          className="course-detail__error-button"
        >
          {t('courseDetail.backToDashboard')}
        </Button>
      </Box>
    );
  }

  // Type guard: course should not be null at this point
  if (!course) {
    return null;
  }

  const courseProgressPct = Math.round(Number(course.progress ?? 0));
  const pendingImpactForDialog = getPendingImpactMeasures(course);
  // L'étape Certificat n'apparaît que pour les formations e-learning / blended.
  const showCertificateStep = certCourseType === 'elearning' || certCourseType === 'blended';

  return (
    <AppLayout>
      <Box className="course-detail__container">
        <Box className="course-detail__header-content">

          {showWelcome && hasWelcomeMessageContent(course.welcomeMessage) && (
            <Box className="course-detail__welcome">
              <div className="course-detail__welcome-card">
                <div className="course-detail__welcome-header">
                  <p className="course-detail__welcome-title">
                    {t('courseDetail.welcomeTitle')}
                  </p>
                  <IconButton
                    onClick={handleDismissWelcome}
                    className="course-detail__welcome-close"
                    aria-label={t('common.close')}
                  >
                    <CloseIcon size={20} color="var(--brand-primary)" />
                  </IconButton>
                </div>

                <div className="course-detail__welcome-content">
                  <MarkdownRenderer
                    content={getLocalizedText(course.welcomeMessage)}
                    sx={{ color: 'inherit' }}
                  />
                </div>
              </div>
            </Box>
          )}

          <Box className="course-detail__header-actions-row">
            <p className="course-detail__title">
              {getLocalizedText(course.title)}
            </p>
            <Button
              variant="contained"
              onClick={handleResume}
              className="button-primary course-detail__resume-button"
            >
              {courseProgressPct === 0
                ? t('courseDetail.start')
                : courseProgressPct >= 100
                  ? t('courseDetail.review')
                  : t('courseDetail.resume')}
            </Button>


          </Box>
          <CourseProgressBar progress={courseProgressPct} size="detail" />
        </Box>
      </Box>

      {/* Course Timeline */}
      <Box className="course-detail__timeline-section" style={{ maxWidth: '840px', margin: '0 auto' }}>
        <p className="course-detail__timeline-title">
          {t('courseDetail.timelineTitle')}
        </p>

        <Box className="course-detail__timeline-container">
          {/* Timeline line */}

          {/* Étape 1 (optionnelle) : diagnostic initial */}
          {course.assessment?.enabled && renderAssessmentStep('diagnostic', { isFirst: true })}

          {(course.chapters || []).map((chapter, index) => {
            const status = getChapterStatus(chapter);
            const isCompleted = status === 'completed';
            const isLocked = status === 'locked';

            // Calculate progress for the chapter
            const totalItems = chapter.items?.length || 0;
            const completedItems = chapter.items?.filter((item: any) => item.isCompleted).length || 0;
            const remainingItems = totalItems - completedItems;
            const progressPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

            // Le premier/dernier chapitre doit garder un trait haut/bas s'il est encadré
            // par une étape d'assessment (diagnostic au-dessus, mesure finale en dessous).
            const hasAssessmentBefore = !!course.assessment?.enabled && index === 0;
            const hasAssessmentAfter =
              (!!course.assessment?.enabled || showCertificateStep) &&
              index === course.chapters.length - 1;
            const showTopTrait = index > 0 || hasAssessmentBefore;
            const showBottomTrait = index < course.chapters.length - 1 || hasAssessmentAfter;

            return (
              <div>
                <div
                  key={chapter._id}
                  className={`course-detail__chapter course-detail__chapter--${status}${!isLocked ? ' course-detail__chapter--clickable' : ''}`}
                  onClick={!isLocked ? () => handleOpenChapter(normalizeId(chapter._id)) : undefined}
                  role={!isLocked ? 'button' : undefined}
                >
                  <div className="course-detail__chapter-icon-container">
                    {showTopTrait ? (
                      <div className="course-detail__chapter-icon-traittop"></div>
                    ) : (
                      <div style={{ height: '10px' }}></div>
                    )}
                    <div className="course-detail__chapter-icon">
                      {getChapterIcon(chapter)}
                    </div>

                    {showBottomTrait ? (
                      <div className="course-detail__chapter-icon-trait"></div>
                    ) : (
                      <div style={{ flex: 1 }}></div>
                    )}
                  </div>
                  {/* Chapter content */}
                  <div className={`course-detail__chapter-card course-detail__chapter-card--${status}`}>
                    <div className="course-detail__chapter-content">
                      <Box className="course-detail__chapter-header">
                        <p className={`course-detail__chapter-surtitle course-detail__chapter-title--${status}`}>
                          {getLocalizedText(chapter.description)}
                        </p>
                        <p className={`course-detail__chapter-title course-detail__chapter-title--${status}`}>
                          {getLocalizedText(chapter.title)}
                        </p>
                        {isLocked && chapter.unlockDate && (
                          <Box className="course-detail__chapter-unlock-message">
                            <InfoIcon size={16} color="#A0A0A0" />
                            <Typography variant="body2" className="course-detail__chapter-unlock-text">
                              {t('courseDetail.chapterUnlock', { date: formatUnlockDate(chapter.unlockDate) })}
                            </Typography>
                          </Box>
                        )}
                        {!isCompleted && chapter.deadline && remainingItems > 0 && (
                          <Box className="course-detail__chapter-deadline-message">
                            <WarningIcon size={16} color="#FF0000" />
                            <Typography variant="body2" className="course-detail__chapter-deadline-text">
                              {remainingItems === 1
                                ? t('courseDetail.chapterDeadlineSingular', { count: remainingItems, date: formatDeadline(chapter.deadline) })
                                : t('courseDetail.chapterDeadlinePlural', { count: remainingItems, date: formatDeadline(chapter.deadline) })
                              }
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <div className="course-detail__chapter-modulescount">
                        {isLocked ? (
                          <div className="course-detail__progress-indicator course-detail__progress-indicator--locked">
                            <LockIcon size={24} color="#FFFFFF" />
                          </div>
                        ) : (
                          <div className="course-detail__progress-indicator course-detail__progress-indicator--in-progress">
                            <p className="course-detail__progress-text">
                              {completedItems}/{totalItems}
                            </p>
                            <div className="course-detail__progress-circle">
                              <CircularProgress
                                aria-hidden
                                variant="determinate"
                                value={100}
                                size={48}
                                thickness={7}
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  color: 'var(--brand-shell-bg)',
                                  '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                  },
                                }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={progressPercentage}
                                size={48}
                                thickness={7}
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  color: 'var(--brand-primary)',
                                  zIndex: 1,
                                  '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'butt',
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {(index < course.chapters.length - 1 || hasAssessmentAfter) && (
                  <div className='course-detail__chapter-undertrait'>
                    <div className='course-detail__chapter-undertrait-line'></div>
                  </div>
                )}
                {chapter.items?.map(it => it.itemType === 'workshop' && (
                  <div>
                    <div key={chapter._id} className={`course-detail__chapter course-detail__chapter--${status}`}>

                      <div className="course-detail__chapter-icon-container">
                        <div className="course-detail__chapter-icon-traittop"></div>
                        <div className="course-detail__chapter-icon">
                          <WorkshopIcon size={36} color="var(--brand-shell-bg)" />
                        </div>
                        <div className="course-detail__chapter-icon-trait">

                        </div>
                      </div>
                      {/* Chapter content */}
                      <div className={`course-detail__chapter-card course-detail__chapter-card--${status}`}>
                        <div className="course-detail__chapter-content">
                          <Box className="course-detail__chapter-header">
                            <p className={`course-detail__chapter-surtitle course-detail__chapter-title--${status}`}>
                              {getLocalizedText(it.description) && it.date && it.startTime && it.endTime
                                ? `${getLocalizedText(it.description)}・${formatWorkshopDate(it.date)}, ${formatTime(it.startTime)} - ${formatTime(it.endTime)}`
                                : getLocalizedText(it.description) || getLocalizedText(it.title)}
                            </p>
                            <p className={`course-detail__chapter-title course-detail__chapter-title--${status}`}>
                              {getLocalizedText(it.title)}
                            </p>
                          </Box>
                        </div>
                      </div>
                    </div>
                    <div className='course-detail__chapter-undertrait'>
                      <div className='course-detail__chapter-undertrait-line'></div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Mesure d'impact finale + Certificat + suivis M+ (optionnels) */}
          {(course.assessment?.enabled || showCertificateStep) &&
            (() => {
              const assessmentEnabled = !!course.assessment?.enabled;
              const fus = assessmentEnabled
                ? [...(course.assessment!.followups || [])]
                    .filter((fu) => fu.submitted || fu.available || fu.pendingDiagnostic)
                    .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
                : [];
              const hasPrecedingStep = assessmentEnabled || (course.chapters || []).length > 0;
              return (
                <>
                  {assessmentEnabled &&
                    renderAssessmentStep('final', {
                      isLast: !showCertificateStep && fus.length === 0,
                    })}
                  {showCertificateStep &&
                    renderCertificateStep({
                      isFirst: !hasPrecedingStep,
                      isLast: fus.length === 0,
                    })}
                  {fus.map((fu, idx) =>
                    renderAssessmentStep('followup', {
                      followup: fu,
                      isLast: idx === fus.length - 1,
                    })
                  )}
                </>
              );
            })()}
        </Box>
      </Box>
      <CourseLanguageDialog
        open={languageDialogOpen}
        availableLanguages={availableLanguages}
        onSelectLanguage={handleSelectCourseLanguage}
      />

      <Dialog
        open={Boolean(impactDialogOpen && pendingImpactForDialog)}
        onClose={dismissImpactPromptForSession}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('courseDetail.impactPrompt.title')}</DialogTitle>
        <DialogContent>
          {pendingImpactForDialog?.pendingFinal &&
            pendingImpactForDialog.pendingFollowups.length === 0 && (
              <Typography variant="body1" sx={{ pt: 0.5 }}>
                {t('courseDetail.impactPrompt.bodyFinalOnly')}
              </Typography>
            )}
          {!pendingImpactForDialog?.pendingFinal &&
            pendingImpactForDialog &&
            pendingImpactForDialog.pendingFollowups.length > 0 && (
              <Typography variant="body1" sx={{ pt: 0.5 }}>
                {t('courseDetail.impactPrompt.bodyFollowupsOnly', {
                  count: pendingImpactForDialog.pendingFollowups.length,
                })}
              </Typography>
            )}
          {pendingImpactForDialog?.pendingFinal &&
            pendingImpactForDialog.pendingFollowups.length > 0 && (
              <Typography variant="body1" sx={{ pt: 0.5 }}>
                {t('courseDetail.impactPrompt.bodyBoth', {
                  count: pendingImpactForDialog.pendingFollowups.length,
                })}
              </Typography>
            )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={dismissImpactPromptForSession} color="inherit">
            {t('courseDetail.impactPrompt.later')}
          </Button>
          {pendingImpactForDialog?.pendingFinal && (
            <Button variant="contained" onClick={goToFinalImpactFromDialog} color="primary">
              {t('courseDetail.impactPrompt.ctaFinal')}
            </Button>
          )}
          {pendingImpactForDialog?.pendingFollowups.map((fu) => (
            <Button
              key={fu.id}
              variant={
                pendingImpactForDialog.pendingFinal && pendingImpactForDialog.pendingFollowups.length > 0
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => goToFollowupImpactFromDialog(fu.id)}
            >
              {getLocalizedText(fu.label, t('courseDetail.impactPrompt.ctaFollowupFallback'))}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};

export default CourseDetail;
